<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  COLOR_TOKENS,
  COLOR_PROFILES,
  resolveColors,
  applyColorPaletteFromSettings,
} from 'dashboard/helper/colorPaletteHelper';

// Tela "Opções de visualização" (antes "Ocultar & Cores"): UI própria pra
// configurar Account.settings.odtravel_hidden_ui (sidebar/labels/
// customAttributes) e o sistema de perfis de cor (odtravel_color_profiles +
// odtravel_active_color_profile), sem precisar de SQL direto nem rebuild do
// Chatwoot. Ver useHiddenUi.js e colorPaletteHelper.js.
//
// Build 6: Administrator sempre ve/edita tudo, independente do que estiver
// marcado aqui. As secoes de ocultar (sidebar/labels/customAttributes/
// actionButtons) so afetam quem nao e admin - ver useHiddenUi.js.
const PAGE_TITLE = 'Opções de visualização';
const SIDEBAR_SECTION_TITLE = 'Ocultar itens do menu lateral';
const LABELS_SECTION_TITLE = 'Ocultar etiquetas';
const ATTRIBUTES_SECTION_TITLE = 'Ocultar atributos customizados';
const ACTION_BUTTONS_SECTION_TITLE = 'Ocultar botões de funções';

const MODES = [
  { key: 'light', label: 'Claro' },
  { key: 'dark', label: 'Escuro' },
];

const SIDEBAR_ITEMS = [
  { name: 'Inbox', label: 'Caixa de Entrada' },
  { name: 'Conversation', label: 'Conversas' },
  { name: 'ODFlow', label: 'ODFlow' },
  { name: 'Cockpit', label: 'Cockpit' },
  { name: 'Captain', label: 'Capitão' },
  { name: 'Calls', label: 'Calls' },
  { name: 'Contacts', label: 'Contatos' },
  { name: 'Companies', label: 'Empresas' },
  { name: 'Reports', label: 'Relatórios' },
  { name: 'Campaigns', label: 'Campanhas' },
  { name: 'Portals', label: 'Central de Ajuda' },
];

const ACTION_BUTTON_ITEMS = [
  { key: 'iaraToggle', label: 'Assistente IAra (liga/desliga)' },
];

const store = useStore();
const getters = useStoreGetters();
const { accountId } = useAccount();

const account = computed(() =>
  getters['accounts/getAccount'].value(accountId.value)
);
const uiFlags = computed(() => getters['accounts/getUIFlags'].value);
const isSaving = computed(() => uiFlags.value.isUpdating);

const labels = computed(() => getters['labels/getLabels'].value);
const customAttributes = computed(
  () => getters['attributes/getAttributes'].value
);

const hiddenSidebar = ref([]);
const hiddenLabels = ref([]);
const hiddenAttributes = ref([]);
const hiddenActionButtons = ref([]);

// { light: profileId, dark: profileId }
const activeProfile = reactive({
  light: 'light-whatsapp',
  dark: 'dark-whatsapp',
});
// { light: { [profileId]: { [tokenKey]: hex } }, dark: {...} }
const overrides = reactive({ light: {}, dark: {} });
// perfil expandido pra edição, por modo
const expandedProfile = reactive({ light: null, dark: null });

function loadFromAccount() {
  const settings = account.value?.settings ?? {};
  const hidden = settings.odtravel_hidden_ui ?? {};
  hiddenSidebar.value = [...(hidden.sidebar ?? [])];
  hiddenLabels.value = [...(hidden.labels ?? [])];
  hiddenAttributes.value = [...(hidden.customAttributes ?? [])];
  hiddenActionButtons.value = [...(hidden.actionButtons ?? [])];

  const active = settings.odtravel_active_color_profile ?? {};
  activeProfile.light = active.light || COLOR_PROFILES.light[0].id;
  activeProfile.dark = active.dark || COLOR_PROFILES.dark[0].id;

  const savedOverrides = settings.odtravel_color_profiles ?? {};
  overrides.light = { ...(savedOverrides.light ?? {}) };
  overrides.dark = { ...(savedOverrides.dark ?? {}) };
}

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('attributes/get');
  loadFromAccount();
});

watch(() => account.value?.settings, loadFromAccount);

function toggle(list, value) {
  const index = list.indexOf(value);
  if (index === -1) {
    list.push(value);
  } else {
    list.splice(index, 1);
  }
}

function colorsForProfile(mode, profileId) {
  return resolveColors(mode, profileId, overrides[mode][profileId] || {});
}

function toggleExpanded(mode, profileId) {
  expandedProfile[mode] =
    expandedProfile[mode] === profileId ? null : profileId;
}

function setTokenColor(mode, profileId, tokenKey, hex) {
  if (!overrides[mode][profileId]) {
    overrides[mode][profileId] = {};
  }
  overrides[mode][profileId] = {
    ...overrides[mode][profileId],
    [tokenKey]: hex,
  };
}

function restoreProfileDefaults(mode, profileId) {
  const next = { ...overrides[mode] };
  delete next[profileId];
  overrides[mode] = next;
}

async function save() {
  try {
    await store.dispatch('accounts/update', {
      odtravel_hidden_ui: {
        sidebar: hiddenSidebar.value,
        labels: hiddenLabels.value,
        customAttributes: hiddenAttributes.value,
        actionButtons: hiddenActionButtons.value,
      },
      odtravel_active_color_profile: { ...activeProfile },
      odtravel_color_profiles: {
        light: { ...overrides.light },
        dark: { ...overrides.dark },
      },
    });
    const isDarkMode = document.body.classList.contains('dark');
    applyColorPaletteFromSettings(
      {
        odtravel_active_color_profile: { ...activeProfile },
        odtravel_color_profiles: {
          light: { ...overrides.light },
          dark: { ...overrides.dark },
        },
      },
      isDarkMode
    );
    useAlert('Configuração salva.');
  } catch {
    useAlert('Não foi possível salvar a configuração.');
  }
}
</script>

<template>
  <div class="flex flex-col w-full max-w-3xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader :title="PAGE_TITLE" />
    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <SectionLayout
        v-for="mode in MODES"
        :key="mode.key"
        :title="`Perfis de cor: modo ${mode.label}`"
        description="Escolha o perfil ativo pra esse modo. Clique num perfil pra abrir e ajustar cada cor individualmente."
        class="!pt-0"
      >
        <div class="grid gap-3">
          <div
            v-for="profile in COLOR_PROFILES[mode.key]"
            :key="profile.id"
            class="border rounded-lg overflow-hidden"
            :class="
              activeProfile[mode.key] === profile.id
                ? 'border-n-brand'
                : 'border-n-weak'
            "
          >
            <div class="flex items-center gap-3 px-3 py-2 bg-n-surface-1">
              <input
                v-model="activeProfile[mode.key]"
                type="radio"
                :name="`active-profile-${mode.key}`"
                :value="profile.id"
              />
              <div class="flex gap-1">
                <span
                  v-for="key in [
                    'dashboard-bg',
                    'brand',
                    'reply-msg-bubble',
                    'reply-iara-accent',
                  ]"
                  :key="key"
                  class="w-4 h-4 rounded-full border border-n-weak"
                  :style="{
                    backgroundColor: colorsForProfile(mode.key, profile.id)[
                      key
                    ],
                  }"
                />
              </div>
              <span class="text-sm font-medium text-n-slate-12 flex-grow">
                {{ profile.name }}
              </span>
              <button
                type="button"
                class="text-xs text-n-slate-11 hover:text-n-slate-12"
                @click="toggleExpanded(mode.key, profile.id)"
              >
                {{
                  expandedProfile[mode.key] === profile.id
                    ? 'Fechar'
                    : 'Editar cores'
                }}
              </button>
            </div>

            <div
              v-if="expandedProfile[mode.key] === profile.id"
              class="p-3 grid gap-4 border-t border-n-weak"
            >
              <div
                v-for="group in [...new Set(COLOR_TOKENS.map(t => t.group))]"
                :key="group"
              >
                <p class="text-xs font-medium text-n-slate-11 mb-2">
                  {{ group }}
                </p>
                <div class="grid gap-2">
                  <label
                    v-for="token in COLOR_TOKENS.filter(t => t.group === group)"
                    :key="token.key"
                    class="flex items-center gap-3 text-sm text-n-slate-12"
                  >
                    <input
                      type="color"
                      class="w-10 h-8 rounded border border-n-weak cursor-pointer shrink-0"
                      :value="colorsForProfile(mode.key, profile.id)[token.key]"
                      @input="
                        setTokenColor(
                          mode.key,
                          profile.id,
                          token.key,
                          $event.target.value
                        )
                      "
                    />
                    {{ token.label }}
                  </label>
                </div>
              </div>
              <div>
                <NextButton
                  slate
                  faded
                  size="small"
                  @click="restoreProfileDefaults(mode.key, profile.id)"
                >
                  {{ 'Retornar cores padrão deste perfil' }}
                </NextButton>
              </div>
            </div>
          </div>
        </div>
      </SectionLayout>

      <SectionLayout
        :title="SIDEBAR_SECTION_TITLE"
        description="Esconde o item sem desativar a funcionalidade por trás."
      >
        <div class="grid grid-cols-2 gap-2">
          <label
            v-for="item in SIDEBAR_ITEMS"
            :key="item.name"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              type="checkbox"
              :checked="hiddenSidebar.includes(item.name)"
              @change="toggle(hiddenSidebar, item.name)"
            />
            {{ item.label }}
          </label>
        </div>
      </SectionLayout>

      <SectionLayout
        :title="LABELS_SECTION_TITLE"
        description="A etiqueta continua existindo, só some da lista lateral."
      >
        <div v-if="!labels.length" class="text-sm text-n-slate-11">
          {{ 'Nenhuma etiqueta cadastrada.' }}
        </div>
        <div v-else class="grid grid-cols-2 gap-2">
          <label
            v-for="label in labels"
            :key="label.id"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              type="checkbox"
              :checked="hiddenLabels.includes(label.title)"
              @change="toggle(hiddenLabels, label.title)"
            />
            {{ label.title }}
          </label>
        </div>
      </SectionLayout>

      <SectionLayout
        :title="ATTRIBUTES_SECTION_TITLE"
        description="Some do painel de detalhes da conversa/contato."
      >
        <div v-if="!customAttributes.length" class="text-sm text-n-slate-11">
          {{ 'Nenhum atributo customizado cadastrado.' }}
        </div>
        <div v-else class="grid grid-cols-2 gap-2">
          <label
            v-for="attribute in customAttributes"
            :key="attribute.id"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              type="checkbox"
              :checked="hiddenAttributes.includes(attribute.attribute_key)"
              @change="toggle(hiddenAttributes, attribute.attribute_key)"
            />
            {{ attribute.attribute_display_name }}
          </label>
        </div>
      </SectionLayout>

      <SectionLayout
        :title="ACTION_BUTTONS_SECTION_TITLE"
        description="Administrador sempre ve e usa normalmente. Some o botao só pra quem não é admin."
      >
        <div class="grid grid-cols-2 gap-2">
          <label
            v-for="item in ACTION_BUTTON_ITEMS"
            :key="item.key"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              type="checkbox"
              :checked="hiddenActionButtons.includes(item.key)"
              @change="toggle(hiddenActionButtons, item.key)"
            />
            {{ item.label }}
          </label>
        </div>
      </SectionLayout>

      <div class="mt-2">
        <NextButton blue :is-loading="isSaving" @click="save">
          {{ 'Salvar' }}
        </NextButton>
      </div>
    </div>
  </div>
</template>
